import type { SaveSegmentViewRequest, SaveSegmentViewResponse } from '@carbonwebmail/contracts'
import type { SegmentFilterRule, SegmentSort } from '@carbonwebmail/domain'
import { SaveViewDialog } from '../../ui/SaveViewDialog.js'

/**
 * Keeping the way the segments board is arranged, as a tab.
 *
 * The asking is `SaveViewDialog`, shared with the contacts board: naming a view
 * is the same act on both, and so is what can go wrong with it. What is here is
 * only what a *segments* view holds — the search, the filter, the order and the
 * hidden columns — and how to say that in words.
 *
 * **Saving a view creates nothing a campaign can be sent to.** The rows below
 * are audiences; this is a way of looking at them.
 */
export function SaveSegmentViewDialog({
  search,
  rules,
  sort,
  hidden,
  onSave,
  onClose,
}: {
  readonly search: string
  readonly rules: readonly SegmentFilterRule[]
  readonly sort: SegmentSort | undefined
  readonly hidden: readonly string[]
  readonly onSave: (view: SaveSegmentViewRequest) => Promise<SaveSegmentViewResponse>
  readonly onClose: () => void
}) {
  return (
    <SaveViewDialog
      keeps={described(search, rules.length, sort !== undefined, hidden.length)}
      onClose={onClose}
      onSave={async (name) => {
        const result = await onSave({
          name,
          search,
          ...(rules.length === 0 ? {} : { filter: { children: [...rules] } }),
          ...(sort === undefined ? {} : { sort }),
          hidden: [...hidden],
        })
        // `not-found` cannot happen while creating; it belongs to replacing one
        // somebody else deleted, which this dialog does not do.
        return result.status === 'name-taken' ? 'name-taken' : 'saved'
      }}
    />
  )
}

/** What the view will hold, in words rather than three values. */
function described(search: string, rules: number, ordered: boolean, hidden: number): string {
  const parts = [
    search.trim() === '' ? 'no search' : `the search for "${search.trim()}"`,
    rules === 0 ? 'no filter' : rules === 1 ? '1 condition' : `${rules} conditions`,
    ordered ? 'the current order' : 'the board’s own order',
  ]
  if (hidden > 0) parts.push(hidden === 1 ? '1 hidden column' : `${hidden} hidden columns`)
  return parts.join(', ')
}
